import React, { FC, useEffect, useRef } from 'react';
import { AppUserRatingsFilters as FiltersType } from '../models/appUserRatingsModels';
import { Button } from '@vadiun/react-components';
import { Field, Form, Formik, FormikProps } from 'formik';
import { KeyboardDatePicker } from 'formik-material-ui-pickers';
import { Select } from 'formik-material-ui';
import { Autocomplete, AutocompleteRenderInputParams } from 'formik-material-ui-lab';
import { FormControl, InputLabel, MenuItem, TextField as MuiTextField } from '@material-ui/core';
import { FilterCategoryTitle } from 'app/viaje/viajeTraslado/viajeTraslado/components/FilterCategoryTitle';
import { useDriverNames } from 'shared/hooks/useDriverNames';
import { useClientNames } from 'shared/hooks/useClientNames';

interface Props {
  onSubmit: (values: FiltersType) => void;
  initialValues: FiltersType;
}

// MUI inputs can't receive null as value — converts null to '' and '' back to null/number
const NumericField = ({
  name,
  label,
  value,
  onChange
}: {
  name: string;
  label: string;
  value: number | null;
  onChange: (val: number | null) => void;
}) => (
  <MuiTextField
    name={name}
    label={label}
    variant="outlined"
    margin="dense"
    type="number"
    fullWidth
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
  />
);

const AppUserRatingsFilters: FC<Props> = ({ onSubmit, initialValues }) => {
  const formRef = useRef<FormikProps<FiltersType>>(null);
  const { driversQuery, getDriverName } = useDriverNames();
  const { clientesQuery, clientprivatesQuery, getClientName } = useClientNames();

  useEffect(() => {
    if (formRef.current) formRef.current.setValues(initialValues);
  }, [initialValues]);

  return (
    <Formik<FiltersType>
      innerRef={formRef}
      initialValues={initialValues}
      enableReinitialize
      onSubmit={onSubmit}
    >
      {({ submitForm, values, setFieldValue }) => (
        <Form className="max-w-lg gap-2 p-8">
          <div>
            <div className="mb-2 border-b-2 border-dashed border-gray-100 pb-2">
              <FilterCategoryTitle title="Período" />
              <div className="grid grid-cols-2 gap-x-3">
                <Field
                  component={KeyboardDatePicker}
                  label="Desde"
                  name="dateFrom"
                  inputVariant="outlined"
                  margin="dense"
                  format="DD/MM/YYYY"
                />
                <Field
                  component={KeyboardDatePicker}
                  label="Hasta"
                  name="dateTo"
                  inputVariant="outlined"
                  margin="dense"
                  format="DD/MM/YYYY"
                />
              </div>
            </div>

            <div className="mb-2 border-b-2 border-dashed border-gray-100 pb-2">
              <FilterCategoryTitle title="Reserva" />
              <NumericField
                name="transfer_reservation_id"
                label="ID Reserva"
                value={values.transfer_reservation_id}
                onChange={(val) => setFieldValue('transfer_reservation_id', val)}
              />
            </div>

            <div className="mb-2 border-b-2 border-dashed border-gray-100 pb-2">
              <FilterCategoryTitle title="Cliente" />
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Tipo de Cliente</InputLabel>
                <Field component={Select} name="client_type" label="Tipo de Cliente">
                  <MenuItem value="AllClient">Todos</MenuItem>
                  <MenuItem value="BusinessClient">Empresa</MenuItem>
                  <MenuItem value="ParticularClient">Particular</MenuItem>
                </Field>
              </FormControl>
              <Field
                name="client_id"
                component={Autocomplete}
                options={
                  values.client_type === 'ParticularClient'
                    ? (clientprivatesQuery.data ?? []).map((x) => x.id)
                    : (clientesQuery.data ?? []).map((x) => x.id)
                }
                getOptionLabel={(option: number) => getClientName(option, values.client_type)}
                renderInput={(params: AutocompleteRenderInputParams) => (
                  <MuiTextField {...params} label="Cliente" variant="outlined" margin="dense" />
                )}
              />
            </div>

            <div className="mb-2 border-b-2 border-dashed border-gray-100 pb-2">
              <FilterCategoryTitle title="Chofer" />
              <Field
                name="driver_id"
                component={Autocomplete}
                options={(driversQuery.data ?? []).map((x) => x.id)}
                getOptionLabel={(option: number) => getDriverName(option)}
                renderInput={(params: AutocompleteRenderInputParams) => (
                  <MuiTextField {...params} label="Chofer" variant="outlined" margin="dense" />
                )}
              />
            </div>

            <div className="mb-2 border-b-2 border-dashed border-gray-100 pb-2">
              <FilterCategoryTitle title="Calificación" />
              <div className="grid grid-cols-2 gap-x-3">
                <NumericField
                  name="score_min"
                  label="Mínimo"
                  value={values.score_min}
                  onChange={(val) => setFieldValue('score_min', val)}
                />
                <NumericField
                  name="score_max"
                  label="Máximo"
                  value={values.score_max}
                  onChange={(val) => setFieldValue('score_max', val)}
                />
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button onClick={submitForm} variant="contained">
                Buscar
              </Button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default AppUserRatingsFilters;
